/**
 * LensFlipCard - Read-only flip card for lens exploration
 *
 * FRONT: Large lens title only (tappable)
 * BACK: Lens pill + insight text (tappable)
 *
 * No selection, no persistence, pure exploration
 */

import React from 'react';

interface LensFlipCardProps {
  category: string;
  categoryDisplay: string;
  insightText: string;
  isFlipped: boolean;
  onFlip: () => void;
}

export default function LensFlipCard({
  category,
  categoryDisplay,
  insightText,
  isFlipped,
  onFlip,
}: LensFlipCardProps) {

  return (
    <div
      className="perspective-container"
      style={{
        perspective: '1000px',
        minHeight: '180px',
      }}
    >
      <div
        className="flip-card-inner relative w-full"
        style={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.25s cubic-bezier(0.4, 0.0, 0.2, 1)',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          minHeight: '180px',
        }}
      >
        {/* FRONT FACE */}
        <div
          className="flip-card-face flip-card-front absolute top-0 left-0 w-full"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            minHeight: '180px',
          }}
        >
          <button
            onClick={onFlip}
            disabled={isFlipped}
            className="w-full h-full min-h-[180px] rounded-xl border flex items-center justify-center text-center
              transition-all duration-200 px-5 py-6 cursor-pointer
              bg-[#1F1F1F] border-[#282828] hover:border-[#383838]"
          >
            <div className="text-[28px] sm:text-[34px] md:text-[40px] font-bold uppercase tracking-wide text-[#E0E0E0]">
              {categoryDisplay}
            </div>
          </button>
        </div>

        {/* BACK FACE */}
        <div
          className="flip-card-face flip-card-back absolute top-0 left-0 w-full"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            minHeight: '180px',
          }}
        >
          <button
            onClick={onFlip}
            disabled={!isFlipped}
            className="w-full h-full min-h-[180px] rounded-xl border flex flex-col cursor-pointer
              transition-all duration-200 p-4 sm:p-5 md:p-6 text-left
              bg-[#1F1F1F] border-[#282828] hover:border-[#383838]"
          >
            {/* Lens Pill */}
            <div className="mb-3 sm:mb-3.5">
              <span className="inline-block px-2.5 py-1 rounded-full text-[10px] sm:text-[11px] font-bold uppercase tracking-wider bg-[#282828] text-[#A0A0A0]">
                {categoryDisplay}
              </span>
            </div>

            {/* Insight Sentence */}
            <p
              className="text-[14px] sm:text-[15px] text-[#E0E0E0]"
              style={{
                lineHeight: '1.5',
              }}
            >
              {insightText}
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
